import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, CATEGORIES, PRIORITIES, STATUSES, statusBadge, priorityBadge, categoryColor, fileSrc, money, CATEGORY_TO_SPECIALTY } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Search, Trash2, Pencil, ImagePlus, Sparkles, Loader2, Mail, FileText, DollarSign, ArrowRight, ListChecks, Circle, Clock, CheckCircle2, Wand2, ChevronRight, AlertTriangle } from "lucide-react";
import { TicketSteps } from "../components/TicketSteps";
import { TicketTimeTracking } from "../components/TicketTimeTracking";
import { TicketPurchaseNotes } from "../components/TicketPurchaseNotes";
import { DailyReports } from "../components/DailyReports";
import { TMBilling } from "../components/TMBilling";
import { ServiceSteps } from "../components/ServiceSteps";
import { MaterialRequestButton } from "../components/MaterialRequestButton";
import TechTicketView from "../components/TechTicketView";
import { WorkAssignments } from "../components/WorkAssignments";

const canEstimate = (role) => ["admin", "manager", "estimator", "pm"].includes(role);

const canManage = (role) => ["admin", "manager", "pm"].includes(role);

const InlineCostSummary = ({ ticketId }) => {
  const { t } = useLang();
  const [cs, setCs] = useState(null);
  useEffect(() => {
    api.get(`/tickets/${ticketId}/cost-summary`).then((r) => setCs(r.summary)).catch(() => {});
  }, [ticketId]);
  if (!cs) return null;
  const hasBudget = (cs.budget_total || 0) > 0;
  const hasReal = (cs.actual_total || 0) > 0 || (cs.tech_hours || 0) > 0;
  if (!hasBudget && !hasReal) return null;

  // T&M mode: no estimate → show a clean "real cost" breakdown (hours + labor + material)
  if (!hasBudget) {
    return (
      <div data-testid={`inline-cost-${ticketId}`} className="px-5 pb-4 -mt-1">
        <div className="bg-white border border-slate-200 rounded-lg p-3 max-w-md" data-testid={`tm-cost-${ticketId}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("realCostTM") || "Real cost (T&M)"}</div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">T&amp;M</span>
          </div>
          <table className="w-full text-sm tabular-nums">
            <tbody>
              <tr>
                <td className="text-slate-600 py-0.5">{t("laborCost") || "Labor cost"}</td>
                <td className="text-right text-xs text-slate-400 pr-2">{(cs.tech_hours || 0).toFixed(2)} h</td>
                <td className="text-right" data-testid={`tm-labor-${ticketId}`}>{money(cs.tech_labor || 0)}</td>
              </tr>
              <tr>
                <td className="text-slate-600 py-0.5">{t("materialCost") || "Material cost"}</td>
                <td></td>
                <td className="text-right" data-testid={`tm-material-${ticketId}`}>{money(cs.actual_material || 0)}</td>
              </tr>
              <tr className="font-bold border-t border-slate-200">
                <td className="pt-1">{t("total")}</td>
                <td></td>
                <td className="text-right pt-1" data-testid={`tm-total-${ticketId}`}>{money(cs.actual_total || 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const loss = cs.variance_total < -0.009;
  return (
    <div data-testid={`inline-cost-${ticketId}`} className="px-5 pb-4 -mt-1">
      <div className="bg-white border border-slate-200 rounded-lg p-3 max-w-md">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{t("costSummary")}</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase text-slate-400">
              <th className="text-left font-bold"></th>
              <th className="text-right font-bold">{t("budget")}</th>
              <th className="text-right font-bold">{t("real")}</th>
              <th className="text-right font-bold">{t("variance")}</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            <tr><td className="text-slate-600">{t("labor")}</td><td className="text-right">{money(cs.budget_labor)}</td><td className="text-right">{money(cs.actual_labor)}</td><td className={`text-right ${cs.variance_labor < 0 ? "text-red-600" : "text-emerald-600"}`}>{money(cs.variance_labor)}</td></tr>
            <tr><td className="text-slate-600">{t("material")}</td><td className="text-right">{money(cs.budget_material)}</td><td className="text-right">{money(cs.actual_material)}</td><td className={`text-right ${cs.variance_material < 0 ? "text-red-600" : "text-emerald-600"}`}>{money(cs.variance_material)}</td></tr>
            <tr className="font-bold border-t border-slate-200"><td>{t("total")}</td><td className="text-right">{money(cs.budget_total)}</td><td className="text-right" data-testid={`inline-cost-actual-${ticketId}`}>{money(cs.actual_total)}</td><td className={`text-right ${loss ? "text-red-600" : "text-emerald-600"}`}>{money(cs.variance_total)}</td></tr>
          </tbody>
        </table>
        {loss && (
          <div data-testid={`loss-banner-${ticketId}`} className="mt-2 flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-2.5 py-2">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <span className="text-xs font-semibold text-red-700">{t("lossBanner")}: {money(Math.abs(cs.variance_total))}. {t("approvedLockedHint")}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{label}</label>
    {children}
  </div>
);
const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

export default function Tickets() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [techs, setTechs] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pmFilter, setPmFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [rawDesc, setRawDesc] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [cost, setCost] = useState(null);
  const [payPeriod, setPayPeriod] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [staff, setStaff] = useState([]);
  const [subs, setSubs] = useState([]);
  const OPTIONAL_COLS = ["realCost", "manager", "pm", "estimatorCol", "tenantCol", "accessCode", "requestDate", "startDate"];
  const [cols, setCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jmo_ticket_cols")) || ["manager", "tenantCol", "startDate"]; }
    catch { return ["manager", "tenantCol", "startDate"]; }
  });
  const [colsOpen, setColsOpen] = useState(false);
  const toggleCol = (c) => {
    const next = cols.includes(c) ? cols.filter((x) => x !== c) : [...cols, c];
    setCols(next);
    localStorage.setItem("jmo_ticket_cols", JSON.stringify(next));
  };
  const has = (c) => user.role !== "client" && cols.includes(c);
  const totalCols = 9 + (user.role !== "client" ? cols.length : 0);

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const load = () => api.get("/tickets").then(setTickets);
  useEffect(() => {
    load();
    if (canManage(user.role) || user.role === "client") {
      api.get("/units").then(setUnits).catch(() => {});
      api.get("/properties").then(setProperties).catch(() => {});
    }
    api.get("/technicians").then(setTechs).catch(() => {});
    if (canManage(user.role)) {
      api.get("/users").then(setStaff).catch(() => {});
      loadSubs();
    }
  }, []); // eslint-disable-line

  // Auto-open a ticket when navigated with ?open=<id>
  useEffect(() => {
    if (!tickets.length) return;
    const params = new URLSearchParams(window.location.search);
    const openId = params.get("open");
    if (!openId) return;
    const tk = tickets.find((x) => x.id === openId);
    if (tk) {
      openEdit(tk);
      // Clean the query string so re-loads don't re-open
      const url = new URL(window.location.href);
      url.searchParams.delete("open");
      window.history.replaceState({}, "", url.pathname + (url.search || ""));
    }
  }, [tickets]); // eslint-disable-line

  const loadSubs = () => api.get("/subcontractors").then(setSubs).catch(() => {});

  const openNew = async () => {
    setEditing(null);
    setForm({ title: "", description: "", category: "general", priority: "medium", property_id: "", unit_id: "", photos: [], requires_estimate: false, service_steps: [], _auto_steps: true });
    setRawDesc("");
    setSuggestions(null);
    setOpen(true);
    // Preload default template for the initial category
    try {
      const r = await api.get("/settings/step-template?category=general");
      setForm((f) => ({ ...f, service_steps: (r.steps || []).map((label) => ({ label })), _auto_steps: true }));
    } catch { /* silent */ }
  };
  const [techViewTicket, setTechViewTicket] = useState(null);

  const openEdit = (tk) => {
    // Technicians, subcontractors and clients get the mobile-friendly view — never the manager editor
    if (["technician", "client", "subcontractor"].includes(user.role)) {
      setTechViewTicket(tk);
      return;
    }
    setEditing(tk);
    setForm({
      ...tk, photos: tk.photos || [], assignee_type: tk.assignee_type || "technician",
      subcontractor_name: tk.subcontractor_name || "", subcontract_value: tk.subcontract_value || 0,
      assigned_techs: tk.assigned_techs || (tk.assigned_to ? [{ id: tk.assigned_to, name: tk.assigned_to_name || "" }] : []),
      work_assignments: tk.work_assignments?.length ? tk.work_assignments
        : (tk.assignee_type === "subcontractor" && (tk.subcontractor_name || tk.subcontract_value)
          ? [{ type: "subcontractor", ref_id: "", name: tk.subcontractor_name || "", trades: [], value: tk.subcontract_value || 0 }]
          : []),
    });
    setTasks(tk.tasks || []);
    setRawDesc("");
    setSuggestions(null);
    setCost(null);
    setOpen(true);
    api.get(`/tickets/${tk.id}/cost-summary`).then(setCost).catch(() => {});
  };

  const loadCost = () => editing && api.get(`/tickets/${editing.id}/cost-summary`).then(setCost).catch(() => {});
  const generatePayment = async () => {
    const due = cost?.summary?.sub_due || 0;
    if (due <= 0) return toast.error(t("due"));
    await api.post(`/tickets/${editing.id}/sub-payment`, { amount: due, period: payPeriod });
    toast.success(t("generatePayment"));
    setPayPeriod("");
    loadCost();
  };

  const refreshTasks = async () => {
    if (!editing) return;
    const fresh = await api.get(`/tickets/${editing.id}`);
    setTasks(fresh.tasks || []);
    load();
    api.get(`/tickets/${editing.id}/cost-summary`).then(setCost).catch(() => {});
  };
  const addTask = async () => {
    if (!newTask.trim()) return;
    await api.post(`/tickets/${editing.id}/tasks`, { title: newTask, kind: "service" });
    setNewTask("");
    refreshTasks();
  };
  const cycleTask = async (tk) => {
    const next = { pending: "in_progress", in_progress: "done", done: "pending" }[tk.status];
    await api.put(`/tickets/${editing.id}/tasks/${tk.id}`, { status: next });
    refreshTasks();
  };
  const removeTask = async (taskId) => {
    await api.del(`/tickets/${editing.id}/tasks/${taskId}`);
    refreshTasks();
  };
  const genTasks = async () => {
    await api.post(`/tickets/${editing.id}/tasks/generate`);
    toast.success(t("saved"));
    refreshTasks();
  };

  const runAiIntake = async () => {
    if (!rawDesc.trim()) return toast.error(t("describeIssue"));
    setAiLoading(true);
    try {
      const res = await api.post("/tickets/ai-intake", { description: rawDesc });
      setForm((f) => ({
        ...f,
        title: res.title || f.title,
        description: res.summary || rawDesc,
        category: res.category || f.category,
        priority: res.priority || f.priority,
      }));
      setSuggestions({ steps: res.suggested_steps || [], materials: res.suggested_materials || [] });
      toast.success(t("aiFilled"));
    } catch (e) {
      toast.error(t("error"));
    } finally {
      setAiLoading(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.upload(file);
      setForm((f) => ({ ...f, photos: [...(f.photos || []), res.url] }));
    } catch {
      toast.error(t("error"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.title?.trim()) return toast.error(t("title"));
    try {
      const unit = units.find((u) => u.id === form.unit_id);
      const prop = properties.find((p) => p.id === form.property_id);
      const payload = {
        ...form,
        unit_name: unit?.name || form.unit_name || "",
        client_id: unit?.client_id || prop?.client_id || form.client_id || null,
      };
      // Clean service_steps: strip empty labels + drop internal flag
      if (Array.isArray(payload.service_steps)) {
        payload.service_steps = payload.service_steps
          .filter((s) => (s.label || "").trim())
          .map((s) => ({ label: s.label.trim() }));
      }
      delete payload._auto_steps;
      if (editing) {
        await api.put(`/tickets/${editing.id}`, payload);
      } else {
        await api.post("/tickets", payload);
      }
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch (e) {
      toast.error(t("error"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.del(`/tickets/${id}`);
    toast.success(t("deleted"));
    load();
  };

  const filtered = tickets.filter(
    (tk) =>
      (!statusFilter || tk.status === statusFilter) &&
      (!pmFilter || tk.pm_id === pmFilter) &&
      (!search ||
        tk.title.toLowerCase().includes(search.toLowerCase()) ||
        tk.number?.toLowerCase().includes(search.toLowerCase()) ||
        tk.unit_name?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("tickets")}</h1>
        {user.role !== "client" && user.role !== "subcontractor" && (
          <button data-testid="new-ticket-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2.5 hover:bg-slate-800 transition-colors">
            <Plus className="w-4 h-4" /> {t("newTicket")}
          </button>
        )}
      </div>

      {canEstimate(user.role) && (
        <div className="flex items-center gap-2 text-sm text-slate-600 bg-blue-50 border border-blue-100 rounded-lg px-4 py-2.5">
          <span className="font-semibold text-blue-700">{t("open")}</span>
          <ArrowRight className="w-4 h-4 text-slate-400" />
          <span className="font-semibold text-blue-700">{t("estimate")}</span>
          <ArrowRight className="w-4 h-4 text-slate-400" />
          <span className="font-semibold text-blue-700">{t("approved")}</span>
          <ArrowRight className="w-4 h-4 text-slate-400" />
          <span className="font-semibold text-blue-700">{t("in_progress")}</span>
          <span className="ml-2 text-xs text-slate-500 hidden md:inline">— {t("flowHint")}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="ticket-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className={`${inputCls} pl-9`} />
        </div>
        <select data-testid="ticket-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} max-w-[200px]`}>
          <option value="">{t("status")}: {t("view")}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(s === "completed" ? "completed_s" : s)}</option>)}
        </select>
        {["admin", "manager"].includes(user.role) && (
          <select data-testid="ticket-pm-filter" value={pmFilter} onChange={(e) => setPmFilter(e.target.value)} className={`${inputCls} max-w-[180px]`}>
            <option value="">PM: {t("view")}</option>
            {staff.filter((u) => ["pm", "admin", "manager"].includes(u.role)).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        {user.role !== "client" && (
          <div className="relative">
            <button data-testid="columns-btn" onClick={() => setColsOpen(!colsOpen)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
              <ListChecks className="w-4 h-4" />{t("columns")}
              {cols.length > 0 && <span className="text-[10px] font-bold bg-slate-900 text-white rounded-full px-1.5 py-0.5">{cols.length}</span>}
            </button>
            {colsOpen && (
              <div data-testid="columns-panel" className="absolute right-0 top-full mt-1 z-40 bg-white border border-slate-200 rounded-xl shadow-lg p-2 w-56">
                {OPTIONAL_COLS.map((c) => (
                  <label key={c} data-testid={`col-toggle-${c}`} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 cursor-pointer text-sm text-slate-700">
                    <input type="checkbox" checked={cols.includes(c)} onChange={() => toggleCol(c)} className="rounded border-slate-300" />
                    {t(c)}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3">{t("number")}</th>
                <th className="text-left px-4 py-3">{t("title")}</th>
                <th className="text-left px-4 py-3">{t("propertyCol")}</th>
                <th className="text-left px-4 py-3">{t("serviceType")}</th>
                <th className="text-left px-4 py-3">{t("priority")}</th>
                <th className="text-left px-4 py-3">{t("status")}</th>
                {user.role !== "subcontractor" && <th className="text-left px-4 py-3">{t("estimate")}</th>}
                <th className="text-left px-4 py-3">{t("assignedTo")}</th>
                {has("realCost") && <th className="text-right px-4 py-3">{t("realCost")}</th>}
                {has("manager") && <th className="text-left px-4 py-3">{t("manager")}</th>}
                {has("pm") && <th className="text-left px-4 py-3">{t("pm")}</th>}
                {has("estimatorCol") && <th className="text-left px-4 py-3">{t("estimatorCol")}</th>}
                {has("tenantCol") && <th className="text-left px-4 py-3">{t("tenantCol")}</th>}
                {has("accessCode") && <th className="text-left px-4 py-3">{t("accessCode")}</th>}
                {has("requestDate") && <th className="text-left px-4 py-3">{t("requestDate")}</th>}
                {has("startDate") && <th className="text-left px-4 py-3">{t("startDate")}</th>}
                <th className="text-right px-4 py-3">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((tk) => (
                <React.Fragment key={tk.id}>
                <tr data-testid={`ticket-row-${tk.id}`} onClick={() => {
                  // Technicians and subcontractors go straight to the mobile view — no expand step
                  if (user.role === "technician" || user.role === "subcontractor") { openEdit(tk); return; }
                  toggleExpand(tk.id);
                }} className={`border-b border-slate-100 transition-colors hover:bg-slate-50 cursor-pointer ${expandedId === tk.id ? "bg-slate-50" : ""}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">
                    <div className="flex items-center gap-1.5">
                      {user.role !== "technician" && user.role !== "subcontractor" && <ChevronRight data-testid={`expand-icon-${tk.id}`} className={`w-3.5 h-3.5 text-slate-400 transition-transform ${expandedId === tk.id ? "rotate-90" : ""}`} />}
                      {tk.number}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">
                      {tk.title}
                      {tk.source === "email" && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600"><Mail className="w-3 h-3" />{t("sourceEmail")}</span>}
                    </div>
                    <div className="text-xs text-slate-400">{tk.unit_name}</div>
                    {tk.tasks_total > 0 && (
                      <div className="flex items-center gap-1.5 mt-1 max-w-[160px]">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${(tk.tasks_done / tk.tasks_total) * 100}%` }} />
                        </div>
                        <span className="text-[10px] text-slate-400 tabular-nums">{tk.tasks_done}/{tk.tasks_total}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {tk.property_name || tk.unit_name ? (
                      <div>
                        <div className="text-xs font-medium text-slate-700">{tk.property_name || "—"}</div>
                        {tk.unit_name && <div className="text-[11px] text-slate-400">{tk.unit_name}</div>}
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded ${categoryColor(tk.category)}`}>{t(tk.category)}</span></td>
                  <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${priorityBadge(tk.priority)}`}>{t(tk.priority)}</span></td>
                  <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(tk.status)}`}>{t(tk.status === "completed" ? "completed_s" : tk.status)}</span></td>
                  {user.role !== "subcontractor" && (
                  <td className="px-4 py-3">
                    {tk.estimate_status ? (
                      <button data-testid={`ticket-estimate-${tk.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/estimates?ticket=${tk.id}`); }} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(tk.estimate_status)}`}>
                        <FileText className="w-3 h-3" />{t(tk.estimate_status)}
                      </button>
                    ) : canEstimate(user.role) ? (
                      <button data-testid={`create-estimate-${tk.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/estimates?ticket=${tk.id}`); }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                        <DollarSign className="w-3 h-3" />{t("createEstimate")}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400">{t("noEstimate")}</span>
                    )}
                  </td>
                  )}
                  <td className="px-4 py-3 text-slate-600">
                    {(tk.assigned_techs?.length ? tk.assigned_techs.map((x) => x.name).join(", ") : tk.assigned_to_name) || <span className="text-slate-400">{t("unassigned")}</span>}
                  </td>
                  {has("realCost") && (
                    <td className="px-4 py-3 text-right tabular-nums" data-testid={`ticket-real-cost-${tk.id}`}>
                      {(tk.actual_total || 0) > 0 || (tk.tech_hours || 0) > 0 ? (
                        <div className="flex flex-col items-end leading-tight">
                          <span className="font-semibold text-slate-800">{money(tk.actual_total || 0)}</span>
                          <span className="text-[10px] text-slate-400">
                            {(tk.tech_hours || 0).toFixed(1)}h · {money(tk.actual_material || 0)} mat
                          </span>
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  )}
                  {has("manager") && <td className="px-4 py-3 text-slate-600">{tk.responsible_name ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700">{tk.responsible_name}</span> : <span className="text-slate-300">—</span>}</td>}
                  {has("pm") && <td className="px-4 py-3 text-slate-600">{tk.pm_name ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700">{tk.pm_name}</span> : <span className="text-slate-300">—</span>}</td>}
                  {has("estimatorCol") && <td className="px-4 py-3 text-slate-600">{tk.estimator_name ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">{tk.estimator_name}</span> : <span className="text-slate-300">—</span>}</td>}
                  {has("tenantCol") && (
                    <td className="px-4 py-3">
                      {tk.tenant_name || tk.tenant_contact ? (
                        <div>
                          <div className="text-slate-700 text-xs font-medium">{tk.tenant_name || "—"}</div>
                          {tk.tenant_contact && <div className="text-[11px] text-slate-400">{tk.tenant_contact}</div>}
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  )}
                  {has("accessCode") && <td className="px-4 py-3">{tk.access_code ? <code className="text-xs font-mono bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded">{tk.access_code}</code> : <span className="text-slate-300">—</span>}</td>}
                  {has("requestDate") && <td className="px-4 py-3 text-xs text-slate-500 tabular-nums">{(tk.created_at || "").slice(0, 10) || "—"}</td>}
                  {has("startDate") && <td className="px-4 py-3 text-xs text-slate-500 tabular-nums">{(tk.scheduled_date || "").slice(0, 10) || <span className="text-slate-300">—</span>}</td>}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {user.role !== "client" && (
                        <button data-testid={`edit-ticket-${tk.id}`} onClick={(e) => { e.stopPropagation(); openEdit(tk); }} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"><Pencil className="w-4 h-4" /></button>
                      )}
                      {canManage(user.role) && (
                        <button data-testid={`delete-ticket-${tk.id}`} onClick={(e) => { e.stopPropagation(); remove(tk.id); }} className="p-1.5 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      )}
                      {user.role === "client" && <span className="text-xs text-slate-300">—</span>}
                    </div>
                  </td>
                </tr>
                {expandedId === tk.id && (
                  <tr data-testid={`ticket-expanded-${tk.id}`} className="border-b border-slate-200 bg-slate-50/70">
                    <td colSpan={totalCols} className="p-0">
                      {user.role !== "client" && (
                        <>
                          <TicketSteps ticketId={tk.id} role={user.role} onChange={load} />
                          {["admin", "manager", "estimator", "purchasing"].includes(user.role) && <InlineCostSummary ticketId={tk.id} />}
                        </>
                      )}
                      {/* Daily Reports panel — shown for everyone (client sees only shared ones) */}
                      <div className="px-4 py-3 bg-white border-t border-slate-100">
                        <DailyReports ticketId={tk.id} />
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={totalCols} className="px-4 py-12 text-center text-slate-400">{t("noData")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-slate-900">{editing ? t("updateCall") : t("createCall")}</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              {!editing && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2">
                  <p className="text-xs text-blue-800">{t("aiIntakeHint")}</p>
                  <textarea data-testid="ai-intake-input" rows={2} value={rawDesc} onChange={(e) => setRawDesc(e.target.value)} placeholder={t("describeIssue")} className={inputCls} />
                  <button data-testid="ai-intake-btn" type="button" onClick={runAiIntake} disabled={aiLoading} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 text-white text-sm font-medium px-3.5 py-2 hover:bg-blue-700 transition-colors disabled:opacity-60">
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {aiLoading ? t("analyzing") : t("aiAssist")}
                  </button>
                </div>
              )}
              <Field label={t("title")}>
                <input data-testid="ticket-form-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
              </Field>
              <Field label={t("description")}>
                <textarea data-testid="ticket-form-desc" rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("category")}>
                  <select data-testid="ticket-form-category" value={form.category} onChange={async (e) => {
                    const cat = e.target.value;
                    const next = { ...form, category: cat };
                    // Auto-load specialty template if editing steps hasn't started
                    if (!editing && (!form.service_steps || form.service_steps.length === 0 || form._auto_steps)) {
                      try {
                        const r = await api.get(`/settings/step-template?category=${encodeURIComponent(cat)}`);
                        next.service_steps = (r.steps || []).map((label) => ({ label }));
                        next._auto_steps = true;
                      } catch { /* silent */ }
                    }
                    setForm(next);
                  }} className={inputCls}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{t(c)}</option>)}
                  </select>
                </Field>
                <Field label={t("priority")}>
                  <select data-testid="ticket-form-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{t(p)}</option>)}
                  </select>
                </Field>
              </div>
              {(canManage(user.role) || user.role === "client") && (
                <div className="grid grid-cols-2 gap-4">
                  <Field label={t("property") || "Property"}>
                    <select data-testid="ticket-form-property" value={form.property_id || ""} onChange={(e) => {
                      const pid = e.target.value;
                      const p = properties.find((x) => x.id === pid);
                      setForm({
                        ...form,
                        property_id: pid,
                        // Reset unit when property changes so we don't keep a stale unit id.
                        unit_id: "",
                        client_id: p?.client_id || form.client_id || null,
                      });
                    }} className={inputCls}>
                      <option value="">{t("selectProperty") || "Select property"}</option>
                      {properties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.address ? ` — ${p.address}` : ""}</option>)}
                    </select>
                  </Field>
                  <Field label={t("unit")}>
                    <select data-testid="ticket-form-unit" value={form.unit_id || ""} onChange={(e) => setForm({ ...form, unit_id: e.target.value })} className={inputCls}>
                      <option value="">
                        {form.property_id
                          ? ((units.filter((u) => u.property_id === form.property_id).length === 0)
                              ? (t("noUnitsUseProperty") || "No units — use property address")
                              : (t("selectUnit") || "Select unit (optional)"))
                          : (t("selectUnit") || "Select unit")}
                      </option>
                      {units
                        .filter((u) => !form.property_id || u.property_id === form.property_id)
                        .map((u) => <option key={u.id} value={u.id}>{u.label || u.name}</option>)}
                    </select>
                  </Field>
                </div>
              )}
              {canManage(user.role) && (
                <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-3 cursor-pointer hover:bg-slate-100 transition-colors" data-testid="ticket-requires-estimate-row">
                  <input
                    type="checkbox"
                    data-testid="ticket-requires-estimate"
                    checked={!!form.requires_estimate}
                    onChange={(e) => setForm({ ...form, requires_estimate: e.target.checked })}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{t("requiresEstimate") || "Requires Estimate (client approval)"}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{t("requiresEstimateHint") || "When checked, this service call needs a written estimate before work starts. Otherwise, bill by Time & Materials at completion."}</div>
                  </div>
                </label>
              )}
              {!editing && canManage(user.role) && (
                <div className="border border-slate-200 rounded-lg p-3.5 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("serviceSteps") || "Service Steps"}</div>
                    <button type="button" onClick={() => setForm({ ...form, service_steps: [...(form.service_steps || []), { label: "" }] })} className="text-xs text-slate-600 hover:text-slate-900 font-medium">+ {t("add") || "Add"}</button>
                  </div>
                  {(form.service_steps || []).length === 0 && (
                    <p className="text-[11px] text-slate-400">{t("stepsAutoHint") || "Steps will be auto-loaded from the specialty template when you pick a category."}</p>
                  )}
                  <div className="space-y-1.5">
                    {(form.service_steps || []).map((s, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`preview-step-${i}`}>
                        <span className="text-xs text-slate-400 w-6 tabular-nums">{i + 1}.</span>
                        <input
                          value={s.label}
                          onChange={(e) => setForm({ ...form, service_steps: form.service_steps.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })}
                          className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm focus:ring-1 focus:ring-slate-900 outline-none"
                          placeholder={t("stepPlaceholder") || "Step name"}
                        />
                        <button type="button" onClick={() => setForm({ ...form, service_steps: form.service_steps.filter((_, j) => j !== i) })} className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50">✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {editing && canManage(user.role) && (
                <div className="grid grid-cols-2 gap-4">
                  <Field label={t("status")}>
                    <select data-testid="ticket-form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                      {STATUSES.map((s) => <option key={s} value={s}>{t(s === "completed" ? "completed_s" : s)}</option>)}
                    </select>
                  </Field>
                  <Field label={t("assignedTo")}>
                    <div className="flex flex-wrap gap-1.5" data-testid="ticket-form-assign">
                      {(() => {
                        const spec = CATEGORY_TO_SPECIALTY[form.category] || "general_repair";
                        const sorted = [...techs].sort((a, b) =>
                          ((b.specialties || []).includes(spec) ? 1 : 0) - ((a.specialties || []).includes(spec) ? 1 : 0));
                        const sel = form.assigned_techs || [];
                        return sorted.map((tc) => {
                          const on = sel.some((x) => x.id === tc.id);
                          const rec = (tc.specialties || []).includes(spec);
                          return (
                            <button key={tc.id} type="button" data-testid={`assign-tech-${tc.id}`}
                              onClick={() => setForm({ ...form, assigned_techs: on ? sel.filter((x) => x.id !== tc.id) : [...sel, { id: tc.id, name: tc.name }] })}
                              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : rec ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                              {on ? "✓ " : rec ? "★ " : ""}{tc.name}
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </Field>
                </div>
              )}
              {canManage(user.role) && (
                <WorkAssignments value={form.work_assignments || []} onChange={(wa) => setForm({ ...form, work_assignments: wa })}
                  techs={techs} subs={subs} onSubsReload={loadSubs} />
              )}
              {editing && canManage(user.role) && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label={t("respManager")}>
                    <select data-testid="ticket-form-responsible" value={form.responsible_id || ""}
                      onChange={(e) => { const u = staff.find((x) => x.id === e.target.value); setForm({ ...form, responsible_id: u?.id || "", responsible_name: u?.name || "" }); }} className={inputCls}>
                      <option value="">—</option>
                      {staff.filter((u) => ["admin", "manager"].includes(u.role)).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </Field>
                  <Field label={t("estimatorField")}>
                    <select data-testid="ticket-form-estimator" value={form.estimator_id || ""}
                      onChange={(e) => { const u = staff.find((x) => x.id === e.target.value); setForm({ ...form, estimator_id: u?.id || "", estimator_name: u?.name || "" }); }} className={inputCls}>
                      <option value="">—</option>
                      {staff.filter((u) => ["estimator", "admin", "manager"].includes(u.role)).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </Field>
                  <Field label={t("projectManager")}>
                    <select data-testid="ticket-form-pm" value={form.pm_id || ""}
                      onChange={(e) => { const u = staff.find((x) => x.id === e.target.value); setForm({ ...form, pm_id: u?.id || "", pm_name: u?.name || "" }); }} className={inputCls}>
                      <option value="">—</option>
                      {staff.filter((u) => ["pm", "admin", "manager"].includes(u.role)).map((u) => <option key={u.id} value={u.id}>{u.name}{u.role === "pm" ? " (PM)" : ""}</option>)}
                    </select>
                  </Field>
                </div>
              )}
              {canManage(user.role) && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label={t("tenantName")}>
                    <input data-testid="ticket-form-tenant-name" value={form.tenant_name || ""} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label={t("tenantContact")}>
                    <input data-testid="ticket-form-tenant-contact" value={form.tenant_contact || ""} onChange={(e) => setForm({ ...form, tenant_contact: e.target.value })} placeholder="(000) 000-0000" className={inputCls} />
                  </Field>
                  <Field label={t("accessCode")}>
                    <input data-testid="ticket-form-access-code" value={form.access_code || ""} onChange={(e) => setForm({ ...form, access_code: e.target.value })} className={inputCls} />
                  </Field>
                </div>
              )}
              {editing && canManage(user.role) && (
                <Field label={t("scheduledDate")}>
                  <input data-testid="ticket-form-date" type="datetime-local" value={form.scheduled_date ? form.scheduled_date.slice(0, 16) : ""} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value ? new Date(e.target.value).toISOString() : null })} className={inputCls} />
                </Field>
              )}
              {user.role === "technician" && editing && (
                <Field label={t("status")}>
                  <select data-testid="ticket-form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                    {STATUSES.map((s) => <option key={s} value={s}>{t(s === "completed" ? "completed_s" : s)}</option>)}
                  </select>
                </Field>
              )}
              {suggestions && (suggestions.steps.length > 0 || suggestions.materials.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {suggestions.steps.length > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{t("suggestedSteps")}</div>
                      <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
                        {suggestions.steps.map((s, i) => <li key={i}>{s}</li>)}
                      </ol>
                    </div>
                  )}
                  {suggestions.materials.length > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{t("suggestedMaterials")}</div>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                        {suggestions.materials.map((m, i) => <li key={i}>{m}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              <Field label={t("photos")}>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(form.photos || []).map((p, i) => (
                    <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                      <img src={fileSrc(p)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"><X className="w-3 h-3 text-white" /></button>
                    </div>
                  ))}
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-slate-600 border border-dashed border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50 cursor-pointer">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {t("addPhoto")}
                  <input data-testid="ticket-photo-input" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                </label>
              </Field>

              {editing && (
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <ListChecks className="w-4 h-4 text-slate-500" />
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("jobSteps")}</span>
                    </div>
                    {canEstimate(user.role) && (
                      <button data-testid="generate-steps-btn" onClick={genTasks} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline">
                        <Wand2 className="w-3.5 h-3.5" /> {t("generateSteps")}
                      </button>
                    )}
                  </div>

                  {tasks.length > 0 && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                        <span>{t("progress")}</span>
                        <span className="font-semibold text-slate-700">{tasks.filter((x) => x.status === "done").length}/{tasks.length}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${(tasks.filter((x) => x.status === "done").length / tasks.length) * 100}%` }} />
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    {tasks.map((tk) => {
                      const icon = tk.status === "done" ? CheckCircle2 : tk.status === "in_progress" ? Clock : Circle;
                      const Icon = icon;
                      const color = tk.status === "done" ? "text-emerald-600" : tk.status === "in_progress" ? "text-blue-600" : "text-slate-300";
                      return (
                        <div key={tk.id} data-testid={`task-${tk.id}`} className="flex items-center gap-2.5 group">
                          <button data-testid={`task-toggle-${tk.id}`} onClick={() => cycleTask(tk)} className={`${color} hover:scale-110 transition-transform`}>
                            <Icon className="w-5 h-5" />
                          </button>
                          <span className={`flex-1 text-sm ${tk.status === "done" ? "line-through text-slate-400" : "text-slate-700"}`}>
                            {tk.title}
                            {tk.kind === "material" && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-600">{t("material")}</span>}
                          </span>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${statusBadge(tk.status)}`}>{t(tk.status)}</span>
                          {canEstimate(user.role) && (
                            <button onClick={() => removeTask(tk.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity"><X className="w-4 h-4" /></button>
                          )}
                        </div>
                      );
                    })}
                    {tasks.length === 0 && <p className="text-xs text-slate-400 py-2">{t("noSteps")}</p>}
                  </div>

                  {canEstimate(user.role) && (
                    <div className="flex gap-2 mt-2">
                      <input data-testid="new-task-input" value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTask())} placeholder={t("addStep")} className={`${inputCls} flex-1`} />
                      <button data-testid="add-task-btn" onClick={addTask} className="rounded-lg px-3 py-2 text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"><Plus className="w-4 h-4" /></button>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 mt-2">{t("stepHint")}</p>
                </div>
              )}

              {editing && (
                <TicketTimeTracking ticketId={editing.id} ticket={editing} onChange={() => { load(); loadCost(); }} />
              )}

              {editing && (
                <>
                  <TicketPurchaseNotes ticketId={editing.id} onChange={() => { load(); loadCost(); }} />
                  <DailyReports ticketId={editing.id} />
                </>
              )}

              {editing && ["admin", "manager"].includes(user.role) && (
                <div className="border-t border-slate-200 pt-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-500" />
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("costSummary")} · {t("subcontractor")}</span>
                  </div>

                  {/* budget vs real (or T&M real-cost breakdown if no estimate) */}
                  {cost?.summary && (cost.summary.budget_total > 0 ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                      <table className="w-full">
                        <thead>
                          <tr className="text-[11px] uppercase text-slate-400">
                            <th className="text-left font-bold"></th>
                            <th className="text-right font-bold">{t("budget")}</th>
                            <th className="text-right font-bold">{t("real")}</th>
                            <th className="text-right font-bold">{t("variance")}</th>
                          </tr>
                        </thead>
                        <tbody className="tabular-nums">
                          <tr><td className="text-slate-600">{t("labor")}</td><td className="text-right">{money(cost.summary.budget_labor)}</td><td className="text-right">{money(cost.summary.actual_labor)}</td><td className={`text-right ${cost.summary.variance_labor < 0 ? "text-red-600" : "text-emerald-600"}`}>{money(cost.summary.variance_labor)}</td></tr>
                          <tr><td className="text-slate-600">{t("material")}</td><td className="text-right">{money(cost.summary.budget_material)}</td><td className="text-right">{money(cost.summary.actual_material)}</td><td className={`text-right ${cost.summary.variance_material < 0 ? "text-red-600" : "text-emerald-600"}`}>{money(cost.summary.variance_material)}</td></tr>
                          <tr className="font-bold border-t border-slate-200"><td>{t("total")}</td><td className="text-right">{money(cost.summary.budget_total)}</td><td className="text-right">{money(cost.summary.actual_total)}</td><td className={`text-right ${cost.summary.variance_total < 0 ? "text-red-600" : "text-emerald-600"}`}>{money(cost.summary.variance_total)}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="bg-blue-50/40 border border-blue-100 rounded-lg p-3 text-sm" data-testid="tm-cost-detail">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("realCostTM") || "Real cost (T&M)"}</div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 bg-white border border-blue-200 rounded-full px-2 py-0.5">T&amp;M</span>
                      </div>
                      <table className="w-full tabular-nums">
                        <tbody>
                          <tr>
                            <td className="text-slate-600 py-1">{t("laborCost") || "Labor cost"}</td>
                            <td className="text-right text-xs text-slate-400 pr-2 py-1">{(cost.summary.tech_hours || 0).toFixed(2)} h</td>
                            <td className="text-right py-1">{money(cost.summary.tech_labor || 0)}</td>
                          </tr>
                          <tr>
                            <td className="text-slate-600 py-1">{t("materialCost") || "Material cost"}</td>
                            <td className="py-1"></td>
                            <td className="text-right py-1">{money(cost.summary.actual_material || 0)}</td>
                          </tr>
                          <tr className="font-bold border-t border-blue-200">
                            <td className="pt-1.5">{t("total")}</td>
                            <td className="pt-1.5"></td>
                            <td className="text-right pt-1.5">{money(cost.summary.actual_total || 0)}</td>
                          </tr>
                        </tbody>
                      </table>
                      <p className="mt-2 text-[11px] text-slate-500">{t("tmCostHint") || "No estimate — cost is accumulated from the technician's time entries and material purchases as work is performed."}</p>
                    </div>
                  ))}

                  {/* subcontractor payment */}
                  {cost?.summary?.is_subcontractor && (
                    <div className="bg-fuchsia-50 border border-fuchsia-100 rounded-lg p-3 space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-600">% {t("executed")}</span><span className="font-semibold">{Math.round((cost.summary.progress || 0) * 100)}%</span></div>
                      <div className="flex justify-between"><span className="text-slate-600">{t("earned")}</span><span className="font-semibold">{money(cost.summary.sub_earned)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-600">{t("paid")}</span><span className="font-semibold">{money(cost.summary.sub_paid)}</span></div>
                      <div className="flex justify-between text-base font-bold text-fuchsia-700 border-t border-fuchsia-200 pt-1"><span>{t("due")}</span><span data-testid="sub-due">{money(cost.summary.sub_due)}</span></div>
                      <div className="flex gap-2 pt-1">
                        <input data-testid="pay-period" value={payPeriod} onChange={(e) => setPayPeriod(e.target.value)} placeholder={t("period")} className={`${inputCls} flex-1`} />
                        <button data-testid="generate-payment" onClick={generatePayment} className="text-xs font-medium bg-fuchsia-600 text-white rounded-lg px-3 py-1.5 hover:bg-fuchsia-700 whitespace-nowrap">{t("generatePayment")}</button>
                      </div>
                      {(cost.sub_payments || []).length > 0 && (
                        <div className="pt-1 space-y-1">
                          {cost.sub_payments.map((p) => (
                            <div key={p.id} className="flex justify-between text-xs text-slate-500"><span>{p.period || p.date?.slice(0, 10)}</span><span>{money(p.amount)}</span></div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {editing && ["admin", "manager", "technician", "pm"].includes(user.role) && (
                <ServiceSteps
                  ticketId={editing.id}
                  steps={editing.service_steps || []}
                  canEdit={["admin", "manager"].includes(user.role) || (user.role === "technician")}
                  onChange={(list) => setEditing({ ...editing, service_steps: list })}
                />
              )}
              {editing && ["admin", "manager", "technician", "pm"].includes(user.role) && (
                <MaterialRequestButton ticketId={editing.id} />
              )}
              {editing && ["admin", "manager"].includes(user.role) && (
                <TMBilling ticketId={editing.id} onInvoiced={() => { load(); loadCost(); }} />
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="ticket-form-submit" onClick={save} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {techViewTicket && (
        <TechTicketView
          ticket={techViewTicket}
          onClose={() => setTechViewTicket(null)}
          onChange={() => load()}
        />
      )}
    </div>
  );
}
